import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import OgShell from '../components/OgShell';
import { useAuthState } from '@/components/feature/AuthGuard';
import { Field, TextArea, ScorePicker, ChipGroup } from '../components/fields';
import { FOLLOWUP_LABELS, OUTCOME_OPTIONS, RECIPIENT_CONSENT_ITEMS, formatDate } from '../lib/constants';
import {
  bindRecipientByToken,
  getSignedMediaUrl,
  recipientGetCase,
  recipientPrepareMediaUpload,
  uploadRecipientFileToSignedUrl,
  recipientFinalizeMediaUpload,
  recipientDeleteMedia,
  recipientConfirmCase,
  recipientSaveConsents,
  recipientSetMediaPermission,
  recipientSubmitFirstPerson,
  recipientSubmitReport,
  recipientWithdrawPublicConsent,
  type RecipientGetCaseResult,
} from '../lib/api';
import MediaSection from '../components/MediaSection';
import type { QiMedia, QiSessionRecord, RecipientConsentInput } from '../lib/types';

type SubmitKind = 'self_report' | 'day7' | 'day30' | 'day90';

const KIND_OPTIONS: { value: SubmitKind; label: string }[] = [
  { value: 'self_report', label: '當下感受' },
  { value: 'day7', label: '7 日追蹤' },
  { value: 'day30', label: '30 日追蹤' },
  { value: 'day90', label: '90 日追蹤' },
];

const EMPTY_CONSENT: RecipientConsentInput = {
  storage_consent: false,
  followup_consent: false,
  reviewer_access: false,
  internal_anonymous_use: false,
  public_anonymous_use: false,
  media_public_use: false,
};

const PRACTITIONER_STAGES = new Set(['pre_condition', 'method', 'immediate_reaction', 'post_condition']);

export default function FollowUpPage() {
  const { token } = useParams();
  const auth = useAuthState();

  const [data, setData] = useState<RecipientGetCaseResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorInfo, setErrorInfo] = useState<{ code: string; message: string } | null>(null);

  // 第一視角補充
  const [before, setBefore] = useState('');
  const [during, setDuring] = useState('');
  const [after, setAfter] = useState('');
  const [lifeFunction, setLifeFunctionNote] = useState('');
  const [firstNote, setFirstNote] = useState('');
  const [firstSaving, setFirstSaving] = useState(false);
  const [firstDone, setFirstDone] = useState<string | null>(null);

  // 追蹤回報
  const [kind, setKind] = useState<SubmitKind>('self_report');
  const [discomfort, setDiscomfort] = useState<number | null>(null);
  const [emotion, setEmotion] = useState<number | null>(null);
  const [lifeFunctionScore, setLifeFunctionScore] = useState<number | null>(null);
  const [outcome, setOutcome] = useState<string[]>([]);
  const [newReactions, setNewReactions] = useState('');
  const [otherTherapy, setOtherTherapy] = useState('');
  const [narrative, setNarrative] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  // 同意
  const [consents, setConsents] = useState<RecipientConsentInput>(EMPTY_CONSENT);
  const [consentSaving, setConsentSaving] = useState(false);
  const [consentError, setConsentError] = useState<string | null>(null);
  const [consentDone, setConsentDone] = useState<string | null>(null);
  const [consentPersisted, setConsentPersisted] = useState(false);
  const [withdrawing, setWithdrawing] = useState(false);
  const [confirmationChecked, setConfirmationChecked] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [confirmationDone, setConfirmationDone] = useState<string | null>(null);

  // 媒體
  const [mediaUrls, setMediaUrls] = useState<Record<number, string>>({});
  const [mediaUploading, setMediaUploading] = useState(false);
  const [deletingMediaId, setDeletingMediaId] = useState<number | null>(null);

  useEffect(() => {
    if (!token) {
      setErrorInfo({ code: 'NO_TOKEN', message: '未提供 token' });
      setLoading(false);
      return;
    }
    let cancelled = false;
    recipientGetCase(token)
      .then(async (d) => {
        if (cancelled) return;
        setData(d);
        if (d.consent) {
          setConsentPersisted(true);
          setConsents({
            storage_consent: d.consent.storage_consent,
            followup_consent: d.consent.followup_consent,
            reviewer_access: d.consent.reviewer_access,
            internal_anonymous_use: d.consent.internal_anonymous_use,
            public_anonymous_use: d.consent.public_anonymous_use,
            media_public_use: d.consent.media_public_use,
          });
        }

        // 載入既有第一視角內容
        const fp = d.records.find((r) => r.record_stage === 'recipient_first_person');
        if (fp?.structured_data) {
          setBefore(String(fp.structured_data.before || ''));
          setDuring(String(fp.structured_data.during || ''));
          setAfter(String(fp.structured_data.after || ''));
          setLifeFunctionNote(String(fp.structured_data.life_function || ''));
        }
        if (fp?.narrative) setFirstNote(fp.narrative);

        // 簽名 URL（媒體）
        const urlMap: Record<number, string> = {};
        await Promise.all(
          d.media.map(async (m) => {
            const suppliedUrl = (m as QiMedia & { signed_url?: string | null }).signed_url;
            if (suppliedUrl) {
              urlMap[m.id] = suppliedUrl;
              return;
            }
            const url = await getSignedMediaUrl(m.file_path);
            if (url) urlMap[m.id] = url;
          }),
        );
        if (!cancelled) setMediaUrls(urlMap);
      })
      .catch((err) => {
        if (cancelled) return;
        const code = (err as { code?: string }).code || 'FETCH_ERROR';
        const message = err instanceof Error ? err.message : '取得案例失敗';
        setErrorInfo({ code, message });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  // 已登入案主自動綁定到本人案例
  useEffect(() => {
    if (!token || !auth.user || !data) return;
    if (data.case.recipient_id && data.case.recipient_id !== auth.user.id) return;
    if (!data.case.recipient_id) {
      bindRecipientByToken(token).then((r) => {
        if (r.bound) {
          setData((prev) => (prev ? { ...prev, case: { ...prev.case, recipient_id: auth.user.id } } : prev));
        }
      }).catch(() => {});
    }
  }, [token, auth.user, data]);

  const handleSubmitFirstPerson = async () => {
    if (!token) return;
    setFirstSaving(true);
    setFirstDone(null);
    try {
      await recipientSubmitFirstPerson(token, {
        before,
        during,
        after,
        life_function: lifeFunction,
        note: firstNote,
      });
      setFirstDone('第一視角補充已保存，感謝你。');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : '保存失敗');
    } finally {
      setFirstSaving(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!token) return;
    setSubmitting(true);
    setSubmitError(null);
    setDone(null);
    try {
      await recipientSubmitReport(token, {
        followup_type: kind,
        scores: { discomfort, emotion, life_function: lifeFunctionScore },
        outcome: outcome[0] || null,
        new_reactions: newReactions,
        other_therapy: otherTherapy,
        narrative,
      });
      setDone(kind === 'self_report' ? '感受回報已送出，感謝你。' : '追蹤回報已送出，感謝你。');
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : '送出失敗');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSaveConsents = async () => {
    if (!token) return;
    setConsentSaving(true);
    setConsentError(null);
    setConsentDone(null);
    try {
      await recipientSaveConsents(token, consents);
      setConsentPersisted(true);
      setConsentDone('首次同意設定已保存，後續案例將依此設定辦理。');
    } catch (err) {
      setConsentError(err instanceof Error ? err.message : '保存失敗');
    } finally {
      setConsentSaving(false);
    }
  };

  const handleWithdrawPublic = async () => {
    if (!token) return;
    setWithdrawing(true);
    setConsentError(null);
    setConsentDone(null);
    try {
      await recipientWithdrawPublicConsent(token);
      setConsents((c) => ({ ...c, public_anonymous_use: false, media_public_use: false }));
      setConsentDone('已撤回匿名公開授權，你的紀錄將不再公開。');
    } catch (err) {
      setConsentError(err instanceof Error ? err.message : '撤回失敗');
    } finally {
      setWithdrawing(false);
    }
  };

  const handleConfirmCase = async () => {
    if (!token || !confirmationChecked) return;
    setConfirming(true);
    setConsentError(null);
    setConfirmationDone(null);
    try {
      const result = await recipientConfirmCase(token);
      setData((prev) => prev ? {
        ...prev,
        case: { ...prev.case, status: 'recipient_confirmed', recipient_confirmed_at: result.confirmed_at },
      } : prev);
      setConfirmationDone('你已確認目前資料完整。之後若有新資料，仍可補充並重新確認。');
    } catch (err) {
      setConsentError(err instanceof Error ? err.message : '確認失敗');
    } finally {
      setConfirming(false);
    }
  };

  const handleMediaUpload = async (files: File[]) => {
    if (!token) return;
    setMediaUploading(true);
    setSubmitError(null);
    try {
      const sessionId = data?.sessions?.[0]?.id ?? null;
      for (const file of files) {
        if (file.size > 100 * 1024 * 1024) throw new Error(`${file.name} 超過 100 MB 上限。`);
        const fileType = file.type.startsWith('video') ? 'video' : file.type.startsWith('image') ? 'image' : file.type.startsWith('audio') ? 'audio' : 'document';
        const prepared = await recipientPrepareMediaUpload(token, {
          file_name: file.name,
          file_type: fileType,
          mime_type: file.type || 'application/octet-stream',
          file_size: file.size,
        });
        await uploadRecipientFileToSignedUrl(prepared.upload_path, prepared.upload_token, file);
        const result = await recipientFinalizeMediaUpload(token, {
          upload_path: prepared.upload_path,
          file_type: fileType,
          session_id: sessionId,
        });
        if (result.media) {
          setData((prev) => (prev ? { ...prev, media: [...prev.media, result.media as QiMedia] } : prev));
          const url = result.media.signed_url || await getSignedMediaUrl(result.media.file_path);
          if (url) setMediaUrls((m) => ({ ...m, [result.media!.id]: url }));
        }
      }
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : '上傳失敗');
    } finally {
      setMediaUploading(false);
    }
  };

  const handleMediaPatch = async (mediaId: number, patch: Parameters<typeof recipientSetMediaPermission>[2]) => {
    if (!token) return;
    try {
      await recipientSetMediaPermission(token, mediaId, patch);
      setData((prev) => {
        if (!prev) return prev;
        return { ...prev, media: prev.media.map((m) => (m.id === mediaId ? { ...m, ...patch } : m)) };
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : '更新媒體授權失敗');
    }
  };

  const handleDeleteMedia = async (mediaId: number) => {
    if (!token) return;
    const target = data?.media.find((item) => item.id === mediaId);
    if (!target || !target.file_path.includes('/recipient/')) return;
    const confirmed = window.confirm('確定刪除此附件？刪除後雙方畫面將不再顯示；若已授權公開，也會同步撤回公開授權。');
    if (!confirmed) return;
    setDeletingMediaId(mediaId);
    setSubmitError(null);
    try {
      await recipientDeleteMedia(token, mediaId);
      setData((prev) => prev ? { ...prev, media: prev.media.filter((item) => item.id !== mediaId) } : prev);
      setMediaUrls((prev) => {
        const next = { ...prev };
        delete next[mediaId];
        return next;
      });
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : '刪除附件失敗');
    } finally {
      setDeletingMediaId(null);
    }
  };

  if (loading) {
    return (
      <OgShell>
        <div className="py-20 text-center">
          <div className="w-10 h-10 mx-auto border-2 border-accent-300 border-t-accent-500 rounded-full animate-spin" />
        </div>
      </OgShell>
    );
  }

  if (errorInfo || !data) {
    return <FriendlyError code={errorInfo?.code} message={errorInfo?.message} />;
  }

  const c = data.case;
  const practitionerRecords = data.records.filter((r) => PRACTITIONER_STAGES.has(r.record_stage));

  return (
    <OgShell>
      <div className="max-w-2xl mx-auto px-4 md:px-6 space-y-6">
        <header className="text-center space-y-2 pt-2">
          <span className="inline-flex items-center gap-1.5 text-xs text-accent-700 bg-accent-100/70 px-3 py-1 rounded-full">
            <i className="ri-flashlight-line" />
            受施者回報
          </span>
          <h1 className="text-xl md:text-2xl text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
            {c.recipient_alias || '受施者'} 的宇炁紀錄
          </h1>
          <p className="text-sm text-foreground-500">{c.case_code || `案例 #${c.id}`}</p>
        </header>

        {/* 案例基本資料（自動帶出，無需重填） */}
        <section className="bg-background-100 border border-foreground-200/50 rounded-xl p-5 grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
          <InfoItem label="施炁者" value={c.practitioner_verified ? (c.practitioner_qualification === 'approved' ? '正式施炁者' : '施炁者') : (c.practitioner_name_snapshot || '施炁者')} />
          <InfoItem label="施炁日期與時間" value={formatDate(c.session_datetime)} />
          <InfoItem label="地點／方式" value={c.location || '—'} />
          {c.referrer_type && <InfoItem label="原始介紹人" value={c.referrer_name_snapshot || c.referrer_type} />}
        </section>

        {/* 施炁者紀錄（唯讀，不可覆蓋） */}
        <section className="space-y-3">
          <h2 className="text-base font-medium text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
            施炁者紀錄
          </h2>
          <p className="text-xs text-foreground-400">以下為施炁者已填寫的內容，此區不可覆蓋。若認為有誤，請見下方「提出更正／異議」。</p>
          {practitionerRecords.length === 0 ? (
            <p className="text-sm text-foreground-400 bg-background-100 border border-foreground-200/50 rounded-xl p-4">施炁者尚未填寫紀錄。</p>
          ) : (
            <div className="space-y-2">
              {practitionerRecords.map((r) => (
                <PractitionerRecord key={r.id} record={r} />
              ))}
            </div>
          )}
        </section>

        {/* 案主第一視角補充（並列保存，不覆蓋施炁者） */}
        <section className="bg-background-100 border border-foreground-200/50 rounded-xl p-5 space-y-4">
          <div>
            <h2 className="text-base font-medium text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
              我的第一視角補充
            </h2>
            <p className="text-xs text-foreground-400 mt-1">以你的角度補充，與施炁者紀錄並列保存，不會互相覆蓋。未填寫的欄位可留白。</p>
          </div>

          <Field label="我接受施炁前的狀況">
            <TextArea value={before} onChange={setBefore} rows={3} placeholder="可留白，之後再補充" />
          </Field>
          <Field label="施炁當下的感受">
            <TextArea value={during} onChange={setDuring} rows={3} placeholder="可留白，之後再補充" />
          </Field>
          <Field label="施炁後的變化">
            <TextArea value={after} onChange={setAfter} rows={3} placeholder="可留白，之後再補充" />
          </Field>
          <Field label="後續生活功能變化">
            <TextArea value={lifeFunction} onChange={setLifeFunctionNote} rows={3} placeholder="可留白，之後再補充" />
          </Field>
          <Field label="我想補充的說明">
            <TextArea value={firstNote} onChange={setFirstNote} rows={3} placeholder="可留白" />
          </Field>

          {firstDone && (
            <p className="text-sm text-emerald-700 bg-emerald-50 rounded-md px-4 py-2.5 flex items-center gap-2">
              <i className="ri-check-line" /> {firstDone}
            </p>
          )}

          <button
            type="button"
            onClick={handleSubmitFirstPerson}
            disabled={firstSaving}
            className="w-full whitespace-nowrap px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm hover:bg-primary-600 transition-colors cursor-pointer disabled:opacity-50"
          >
            {firstSaving ? '保存中…' : '保存我的第一視角補充'}
          </button>

          {/* 更正／異議 */}
          <button
            type="button"
            onClick={() => setSubmitError('如需對施炁者紀錄提出更正或異議，請聯絡施炁者或 ABAA 處理。系統會保留雙方原始版本及處理結果。')}
            className="w-full whitespace-nowrap px-4 py-2 rounded-md border border-foreground-200 text-xs text-foreground-500 hover:bg-foreground-50 transition-colors cursor-pointer"
          >
            對此內容提出更正／異議
          </button>
        </section>

        {/* 證據與附件（案主第一視角可上傳，逐檔授權） */}
        <section className="bg-background-100 border border-foreground-200/50 rounded-xl p-5">
          <MediaSection
            media={data.media}
            mediaUrls={mediaUrls}
            uploading={mediaUploading}
            onUpload={handleMediaUpload}
            onPatch={handleMediaPatch}
            onDelete={handleDeleteMedia}
            deletingMediaId={deletingMediaId}
            isRecipient
            mediaPublicConsent={consents.media_public_use}
          />
        </section>

        {/* 追蹤回報 */}
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="bg-background-100 border border-foreground-200/50 rounded-xl p-5 space-y-5">
            <div>
              <h2 className="text-base font-medium text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>
                追蹤回報
              </h2>
              <p className="text-xs text-foreground-400 mt-1">未填寫的欄位可留白，不需強制輸入「無」。</p>
            </div>

            <Field label="這份回報屬於">
              <ChipGroup
                options={KIND_OPTIONS.map((o) => o.label)}
                values={KIND_OPTIONS.filter((o) => o.value === kind).map((o) => o.label)}
                onChange={(labels) => {
                  const matched = KIND_OPTIONS.find((o) => labels.includes(o.label));
                  setKind(matched?.value || 'self_report');
                }}
                multiple={false}
              />
            </Field>

            <ScorePicker label="目前身體不適程度" value={discomfort} onChange={setDiscomfort} />
            <ScorePicker label="情緒狀態" value={emotion} onChange={setEmotion} />
            <ScorePicker label="活動與生活功能" value={lifeFunctionScore} onChange={setLifeFunctionScore} />

            <Field label="原有問題">
              <ChipGroup options={OUTCOME_OPTIONS} values={outcome} onChange={setOutcome} multiple={false} />
            </Field>

            <Field label="是否出現新的反應">
              <TextArea value={newReactions} onChange={setNewReactions} rows={2} placeholder="可留白" />
            </Field>

            <Field label="這段期間是否接受其他療法">
              <TextArea value={otherTherapy} onChange={setOtherTherapy} rows={2} placeholder="可留白" />
            </Field>

            <Field label="當事人自由描述">
              <TextArea value={narrative} onChange={setNarrative} rows={4} placeholder="可留白" />
            </Field>
          </div>

          {submitError && <p className="text-sm text-rose-600 bg-rose-50 rounded-md px-4 py-2.5">{submitError}</p>}
          {done && (
            <p className="text-sm text-emerald-700 bg-emerald-50 rounded-md px-4 py-2.5 flex items-center gap-2">
              <i className="ri-check-line" /> {done}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="w-full whitespace-nowrap px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm md:text-base hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-50"
          >
            {submitting ? '送出中…' : '送出回報'}
          </button>
        </form>

        {/* 同意 */}
        <section className="bg-background-100 border border-foreground-200/50 rounded-xl p-5 space-y-4">
          <div>
            <h2 className="text-base text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
              首次同意設定
            </h2>
            <p className="text-xs text-foreground-500 mt-1">
              以下設定只需第一次保存，後續案例沿用；露臉或含可辨識資訊的照片、影片仍需逐則確認。公開見證會移除姓名、聯絡方式及其他可辨識資料。
            </p>
          </div>

          <div className="space-y-3">
            {RECIPIENT_CONSENT_ITEMS.map((item) => (
              <label key={item.key} className="flex items-start gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={consents[item.key]}
                  onChange={(e) => {
                    setConsentPersisted(false);
                    setConsents((cc) => ({ ...cc, [item.key]: e.target.checked }));
                  }}
                  className="mt-0.5 w-4 h-4 accent-accent-500"
                />
                <span className="text-sm text-foreground-700">{item.label}</span>
              </label>
            ))}
          </div>

          {consentError && <p className="text-sm text-rose-600 bg-rose-50 rounded-md px-4 py-2.5">{consentError}</p>}
          {consentDone && (
            <p className="text-sm text-emerald-700 bg-emerald-50 rounded-md px-4 py-2.5 flex items-center gap-2">
              <i className="ri-check-line" /> {consentDone}
            </p>
          )}

          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={handleSaveConsents}
              disabled={consentSaving}
              className="flex-1 whitespace-nowrap px-6 py-3 rounded-md bg-accent-500 text-background-50 text-sm hover:bg-accent-600 transition-colors cursor-pointer disabled:opacity-50"
            >
              {consentSaving ? '保存中…' : '保存同意'}
            </button>
            <button
              type="button"
              onClick={handleWithdrawPublic}
              disabled={withdrawing}
              className="flex-1 whitespace-nowrap px-6 py-3 rounded-md bg-background-200 text-foreground-700 text-sm hover:bg-background-300 transition-colors cursor-pointer disabled:opacity-50"
            >
              {withdrawing ? '撤回中…' : '撤回匿名公開授權'}
            </button>
          </div>
        </section>

        {/* 案主完整確認：與公開授權分開，補充資料後由案主明確完成交接。 */}
        <section className="rounded-xl border border-primary-200 bg-primary-50/50 p-5 space-y-4">
          <div>
            <h2 className="text-base text-foreground-900" style={{ fontFamily: 'var(--font-heading)' }}>
              案主補充與確認
            </h2>
            <p className="text-xs text-foreground-500 mt-1">
              你可確認目前資料，或在看到匿名見證後隨時回來補充、更正；此步驟不會阻擋施炁者送 ABAA 審核。
            </p>
          </div>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={confirmationChecked}
              onChange={(event) => setConfirmationChecked(event.target.checked)}
              className="mt-0.5 w-4 h-4 accent-primary-500"
            />
            <span className="text-sm text-foreground-700">
              我確認目前由我提供及補充的資料正確、完整；若日後有新資料，我仍可繼續補充並重新確認。
            </span>
          </label>

          {confirmationDone && (
            <p className="text-sm text-emerald-700 bg-emerald-50 rounded-md px-4 py-2.5 flex items-start gap-2">
              <i className="ri-checkbox-circle-line mt-0.5" /> {confirmationDone}
            </p>
          )}

          <button
            type="button"
            onClick={handleConfirmCase}
            disabled={confirming || !confirmationChecked || !consentPersisted || !consents.storage_consent || !consents.reviewer_access}
            className="w-full px-6 py-3 rounded-md bg-primary-600 text-white text-sm hover:bg-primary-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {confirming ? '確認中…' : data.case.recipient_confirmed_at ? '重新確認目前資料' : '確認目前資料正確、完整'}
          </button>
          {(!consents.storage_consent || !consents.reviewer_access) && (
            <p className="text-xs text-amber-700">需先勾選並保存「保存紀錄」及「Reviewer／ABAA 查看」。其他公開選項仍由你自行決定。</p>
          )}
        </section>
      </div>
    </OgShell>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-xs text-foreground-400">{label}</p>
      <p className="text-foreground-700 mt-0.5">{value || '—'}</p>
    </div>
  );
}

function PractitionerRecord({ record }: { record: QiSessionRecord }) {
  const stageLabel: Record<string, string> = {
    pre_condition: '施炁前',
    method: '施炁方式',
    immediate_reaction: '當下反應',
    post_condition: '施炁後',
  };
  return (
    <div className="bg-background-50 border border-foreground-200/50 rounded-lg p-4 space-y-1.5">
      <p className="text-xs font-medium text-accent-700">{stageLabel[record.record_stage] || record.record_stage}</p>
      {record.narrative && <p className="text-sm text-foreground-700 leading-relaxed">{record.narrative}</p>}
      {record.structured_data && <StructuredData data={record.structured_data} />}
      <p className="text-[10px] text-foreground-400">{formatDate(record.created_at)}</p>
    </div>
  );
}

const FIELD_LABELS: Record<string, string> = {
  main_issue: '主要問題',
  body_parts: '身體部位',
  discomfort: '不適程度',
  emotion: '情緒',
  function: '生活功能',
  history: '舊傷／事件',
  treatment: '現行療法',
  start: '開始',
  end: '結束',
  contact: '接觸方式',
  position: '施炁位置',
  audio: '音頻／設備',
  stopped: '中途停止',
  attention: '需注意',
  reactions: '當下反應',
  other: '其他',
  duration: '持續時間',
  recipient_note: '受施者描述',
  recipient_change: '本人感受變化',
  new_discomfort: '新不適',
  need_help: '需專業協助',
  next_step: '下一步',
  followup_24h: '24 小時回報',
};

function StructuredData({ data }: { data: Record<string, unknown> }) {
  const entries = Object.entries(data).filter(([, v]) => v !== null && v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0));
  if (entries.length === 0) return null;
  return (
    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
      {entries.map(([key, val]) => {
        const label = FIELD_LABELS[key] || key;
        let text: string;
        if (typeof val === 'boolean') text = val ? '是' : '否';
        else if (Array.isArray(val)) text = val.join('、');
        else if (typeof val === 'number') text = ['discomfort', 'emotion', 'function', 'life_function'].includes(key) ? `${val} / 10` : String(val);
        else text = String(val);
        return (
          <div key={key} className="text-xs">
            <span className="text-foreground-400">{label}：</span>
            <span className="text-foreground-700">{text || '—'}</span>
          </div>
        );
      })}
    </div>
  );
}

function FriendlyError({ code, message }: { code?: string; message?: string }) {
  const map: Record<string, { icon: string; title: string; hint: string }> = {
    TOKEN_REVOKED: { icon: 'ri-lock-line', title: '連結已撤銷', hint: '此回報連結已被施炁者撤銷。請聯絡施炁者重新產生安全連結。' },
    TOKEN_EXPIRED: { icon: 'ri-time-line', title: '連結已過期', hint: '此回報連結已超過有效期限。請聯絡施炁者重新產生安全連結。' },
    TOKEN_INVALID: { icon: 'ri-links-line', title: '連結無效', hint: '請向施炁者確認正確的回報連結，或請施炁者重新產生安全連結。' },
    NO_TOKEN: { icon: 'ri-links-line', title: '連結無效', hint: '此連結缺少必要的安全憑證，請向施炁者確認完整連結。' },
    INTERNAL_ERROR: { icon: 'ri-error-warning-line', title: '系統暫時無法讀取', hint: '請稍後重試，或聯絡施炁者確認連結狀態。' },
    CONFIG_ERROR: { icon: 'ri-error-warning-line', title: '系統暫時無法讀取', hint: '請稍後重試，或聯絡施炁者確認連結狀態。' },
  };
  const item = map[code || ''] || {
    icon: 'ri-links-line',
    title: message || '連結無效或已失效',
    hint: '請向施炁者確認正確的回報連結。',
  };

  return (
    <OgShell>
      <div className="max-w-lg mx-auto px-4 py-16 text-center space-y-4">
        <div className="w-16 h-16 mx-auto rounded-full bg-foreground-100 flex items-center justify-center">
          <i className={`${item.icon} text-foreground-400 text-2xl`} />
        </div>
        <h1 className="text-lg text-foreground-800" style={{ fontFamily: 'var(--font-heading)' }}>{item.title}</h1>
        <p className="text-sm text-foreground-500">{item.hint}</p>
      </div>
    </OgShell>
  );
}
